import { type FC, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BrainIcon,
  Calendar,
  ChevronsUpDown,
  FileText,
  Globe,
  HomeIcon,
  InboxIcon,
  Lock,
  LogIn,
  LogOut,
  MessageSquareText,
  MoreHorizontalIcon,
  PencilIcon,
  ClipboardCopy,
  RefreshCw,
  SettingsIcon,
  SquarePenIcon,
  TrashIcon,
} from 'lucide-react';
import { useAssistantRuntime, useThreadList } from '@assistant-ui/react';
import { cn } from '@/client/lib/utils';
import { toast } from 'sonner';
import { listThreads, updateThreadMetadata } from '@/client/lib/chatApi';
import { isBackgroundThreadMetadata } from '@/client/lib/backgroundThreads';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/client/components/ui/dropdown-menu';

type FreshThread = {
  remoteId: string;
  title: string;
  visibility?: 'public' | 'private';
  updatedAt?: string;
};

function relativeTime(dateStr?: string): string {
  if (!dateStr) return 'Start a conversation';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function getTimeGroup(dateStr?: string): string {
  if (!dateStr) return 'Older';
  const date = new Date(dateStr);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday.getTime() - 86400000);
  const startOf7DaysAgo = new Date(startOfToday.getTime() - 7 * 86400000);
  const startOf30DaysAgo = new Date(startOfToday.getTime() - 30 * 86400000);

  if (date >= startOfToday) return 'Today';
  if (date >= startOfYesterday) return 'Yesterday';
  if (date >= startOf7DaysAgo) return 'Previous 7 days';
  if (date >= startOf30DaysAgo) return 'Previous 30 days';
  return 'Older';
}

type GroupedThreads = { label: string; threads: FreshThread[] }[];

function groupByTime(threads: FreshThread[]): GroupedThreads {
  const order = ['Today', 'Yesterday', 'Previous 7 days', 'Previous 30 days', 'Older'];
  const groups = new Map<string, FreshThread[]>();
  for (const t of threads) {
    const label = getTimeGroup(t.updatedAt);
    const arr = groups.get(label);
    if (arr) arr.push(t);
    else groups.set(label, [t]);
  }
  return order.filter((l) => groups.has(l)).map((l) => ({ label: l, threads: groups.get(l)! }));
}

export type ThreadSelectorSelection =
  | { kind: 'existing'; remoteId: string }
  | { kind: 'new'; remoteId: null };

export type ThreadSelectorProps = {
  onNewThread?: () => void;
  actions?: ReactNode;
  className?: string;
  /** Show "All Conversations" button when provided (widget only). */
  onHomeClick?: () => void;
  /** Show "Inbox" button when provided (widget only). */
  onInboxClick?: () => void;
  /** Show "Documents" button when provided (widget only). */
  onDocumentsClick?: () => void;
  /** Show "Settings" button when provided (widget only). */
  onSettingsClick?: () => void;
  /** Show "Scheduled runs" button when provided (widget only). */
  onScheduledRunsClick?: () => void;
  /** Show "Memory" button when provided (widget only). */
  onMemoryClick?: () => void;
  /** Active widget view — changes the trigger label when not on chat. */
  activeView?: 'chat' | 'home' | 'inbox' | 'settings' | 'documents' | 'scheduledRuns' | 'memory';
  /** Called after a thread is selected from the dropdown. */
  onThreadSelect?: (selection: ThreadSelectorSelection) => void;
  /** Show profile section when provided (widget only). */
  embedUser?: {
    name: string | null;
    email: string | null;
    picture?: string;
    role?: string;
  };
  /** Called when user clicks sign-out in the profile section (widget only). */
  onSignOut?: () => void;
  /** Called when user clicks sign-in in the profile section (widget only, when not signed in with Google). */
  onSignIn?: () => void;
  /** Called when user clicks refresh in the profile section. Shows a refresh button next to the avatar when provided. */
  onRefresh?: () => void;
  /** Max number of threads to display in the dropdown (default: unlimited). */
  maxThreads?: number;
};

export const ThreadSelector: FC<ThreadSelectorProps> = ({ onNewThread, actions, className, onHomeClick, onInboxClick, onDocumentsClick, onSettingsClick, onScheduledRunsClick, onMemoryClick, activeView, onThreadSelect, embedUser, onSignOut, onSignIn, onRefresh, maxThreads }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [freshThreads, setFreshThreads] = useState<FreshThread[] | null>(null);
  const refreshing = useRef(false);
  const runtime = useAssistantRuntime();
  const threadIds = useThreadList((s) => s.threadIds);
  const threadItems = useThreadList((s) => s.threadItems);
  const mainThreadId = useThreadList((s) => s.mainThreadId);

  const currentItem = threadItems[mainThreadId];
  const currentRemoteId = currentItem?.remoteId;
  const currentTitle = currentItem?.title || 'New Chat';
  const currentUpdatedAt = freshThreads?.find((t) => t.remoteId === currentRemoteId)?.updatedAt;
  const currentSubtitle = currentRemoteId
    ? currentUpdatedAt
      ? relativeTime(currentUpdatedAt)
      : ''
    : 'Start a conversation';

  const refreshThreadList = useCallback(async () => {
    if (refreshing.current) return;
    refreshing.current = true;
    try {
      const results = (await listThreads('personal', undefined, maxThreads)).filter(
        (thread) => !isBackgroundThreadMetadata(thread.metadata),
      );
      const sorted = [...results].sort(
        (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      );

      const existingTitles = new Map<string, string>();
      for (const id of threadIds) {
        const item = threadItems[id];
        if (item?.remoteId && item.title) {
          existingTitles.set(item.remoteId, item.title);
        }
      }

      setFreshThreads(
        sorted.map((t) => {
          const meta = t.metadata as {
            conversation_title?: string;
            gorgias_user_id?: string;
            visibility?: 'public' | 'private';
          };
          return {
            remoteId: t.thread_id,
            title: existingTitles.get(t.thread_id) || meta?.conversation_title || 'Untitled',
            visibility: meta?.visibility ?? (meta?.gorgias_user_id ? 'private' : 'public'),
            updatedAt: t.updated_at,
          };
        }),
      );
    } catch (e) {
      console.error('Failed to refresh thread list:', e);
    } finally {
      refreshing.current = false;
    }
  }, [threadIds, threadItems, maxThreads]);

  // Keep a stable ref to avoid re-triggering the effect when the callback is recreated
  const refreshFnRef = useRef(refreshThreadList);
  refreshFnRef.current = refreshThreadList;

  // Auto-refresh thread data when the active thread changes (updates subtitle)
  useEffect(() => {
    if (currentRemoteId) {
      refreshFnRef.current();
    }
  }, [currentRemoteId]);

  const displayThreads: FreshThread[] = useMemo(() => {
    const threads =
      freshThreads ??
      threadIds
        .map((id) => {
          const item = threadItems[id];
          if (!item?.remoteId) return null;
          return { remoteId: item.remoteId, title: item.title || 'Untitled' };
        })
        .filter((t): t is FreshThread => t !== null);

    let filtered = threads;
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = threads.filter((t) => t.title.toLowerCase().includes(q));
    }

    return maxThreads ? filtered.slice(0, maxThreads) : filtered;
  }, [freshThreads, threadIds, threadItems, search, maxThreads]);

  const handleNewThread = () => {
    onNewThread?.();
    runtime.threads.switchToNewThread();
    onThreadSelect?.({ kind: 'new', remoteId: null });
    setOpen(false);
  };

  const handleSelect = (remoteId: string) => {
    runtime.threads.switchToThread(remoteId);
    onThreadSelect?.({ kind: 'existing', remoteId });
    setOpen(false);
  };

  const handleDelete = async (remoteId: string) => {
    const isDeletingCurrent = remoteId === currentRemoteId;
    try {
      const internalId = threadIds.find((id) => threadItems[id]?.remoteId === remoteId);
      if (internalId) {
        const item = runtime.threads.getItemById(internalId);
        if (item) await item.delete();
      }
      setFreshThreads((prev) => (prev ? prev.filter((t) => t.remoteId !== remoteId) : prev));
      toast.success('Conversation deleted');
    } catch (err) {
      console.error('Failed to delete conversation:', err);
      toast.error('Failed to delete conversation');
    } finally {
      // Always ensure the user has an active chat — never leave a blank screen
      if (isDeletingCurrent) {
        runtime.threads.switchToNewThread();
      }
      setOpen(false);
    }
  };

  const handleToggleVisibility = async (remoteId: string, currentVisibility: string | undefined) => {
    const newVisibility = currentVisibility === 'public' ? 'private' : 'public';
    await updateThreadMetadata(remoteId, { visibility: newVisibility });
    setFreshThreads((prev) =>
      prev ? prev.map((t) => (t.remoteId === remoteId ? { ...t, visibility: newVisibility } : t)) : prev,
    );
    toast.success(`Conversation is now ${newVisibility}`);
  };

  // Inline rename state
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  // Synchronous ref so handleOpenChange sees the value immediately (before React commits)
  const renamingRef = useRef(false);

  const handleRenameStart = (remoteId: string, currentTitle: string) => {
    renamingRef.current = true;
    setRenamingId(remoteId);
    setRenameValue(currentTitle);
  };

  const handleRenameSave = async () => {
    const trimmed = renameValue.trim();
    const target = renamingId;
    renamingRef.current = false;
    setRenamingId(null);
    if (!trimmed || !target) return;
    await updateThreadMetadata(target, { conversation_title: trimmed });
    setFreshThreads((prev) =>
      prev ? prev.map((t) => (t.remoteId === target ? { ...t, title: trimmed } : t)) : prev,
    );
    toast.success('Conversation renamed');
    const internalId = threadIds.find((id) => threadItems[id]?.remoteId === target);
    if (internalId) {
      void runtime.threads.getItemById(internalId).rename(trimmed);
    }
  };

  const handleRenameCancel = () => {
    renamingRef.current = false;
    setRenamingId(null);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && renamingRef.current) {
      // Save the rename then close
      void handleRenameSave();
    }
    setOpen(isOpen);
    if (isOpen) {
      setSearch('');
      renamingRef.current = false;
      setRenamingId(null);
      refreshThreadList();
    }
  };

  return (
    <div className={cn('flex shrink-0 items-center gap-2.5 border-b px-2 py-[9px]', className)}>
      <div className="min-w-0 flex-1">
        <DropdownMenu open={open} onOpenChange={handleOpenChange}>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                'flex w-full min-w-0 cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted focus:outline-none',
                open && 'bg-muted',
              )}
            >
              {activeView === 'home' ? (
                <>
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md border bg-background">
                    <HomeIcon className="size-4" />
                  </div>
                  <div className="grid flex-1 min-w-0 text-sm leading-tight">
                    <span className="truncate font-medium">All conversations</span>
                    <span className="truncate text-xs text-muted-foreground">Browse and manage threads</span>
                  </div>
                </>
              ) : activeView === 'inbox' ? (
                <>
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md border bg-background">
                    <InboxIcon className="size-4" />
                  </div>
                  <div className="grid flex-1 min-w-0 text-sm leading-tight">
                    <span className="truncate font-medium">Inbox</span>
                    <span className="truncate text-xs text-muted-foreground">Threads awaiting your review</span>
                  </div>
                </>
              ) : activeView === 'documents' ? (
                <>
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md border bg-background">
                    <FileText className="size-4" />
                  </div>
                  <div className="grid flex-1 min-w-0 text-sm leading-tight">
                    <span className="truncate font-medium">Documents</span>
                    <span className="truncate text-xs text-muted-foreground">Browse account files</span>
                  </div>
                </>
              ) : activeView === 'settings' ? (
                <>
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md border bg-background">
                    <SettingsIcon className="size-4" />
                  </div>
                  <div className="grid flex-1 min-w-0 text-sm leading-tight">
                    <span className="truncate font-medium">Settings</span>
                    <span className="truncate text-xs text-muted-foreground">Configure your preferences</span>
                  </div>
                </>
              ) : activeView === 'scheduledRuns' ? (
                <>
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md border bg-background">
                    <Calendar className="size-4" />
                  </div>
                  <div className="grid flex-1 min-w-0 text-sm leading-tight">
                    <span className="truncate font-medium">Scheduled runs</span>
                    <span className="truncate text-xs text-muted-foreground">Recurring Gaia tasks for this account</span>
                  </div>
                </>
              ) : activeView === 'memory' ? (
                <>
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md border bg-background">
                    <BrainIcon className="size-4" />
                  </div>
                  <div className="grid flex-1 min-w-0 text-sm leading-tight">
                    <span className="truncate font-medium">Memory</span>
                    <span className="truncate text-xs text-muted-foreground">What Gaia remembers about you</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md border bg-background">
                    <MessageSquareText className="size-4" />
                  </div>
                  <div className="grid flex-1 min-w-0 text-sm leading-tight">
                    <span className="truncate font-medium">{currentTitle}</span>
                    <span className="truncate text-xs text-muted-foreground">{currentSubtitle}</span>
                  </div>
                </>
              )}
              <ChevronsUpDown className="ml-auto size-4 shrink-0 opacity-50" />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-56 rounded-lg"
            align="start"
            sideOffset={4}
          >
          {/* Search input */}
          <div className="px-2 py-1.5">
            <input
              type="text"
              placeholder="Search threads..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              className="w-full rounded-md border bg-transparent px-2 py-1 text-sm outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-ring"
            />
          </div>

          {displayThreads.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <div className="max-h-64 overflow-y-auto">
                {groupByTime(displayThreads).map((group) => (
                  <div key={group.label}>
                    <DropdownMenuLabel className="text-xs text-muted-foreground">
                      {group.label}
                    </DropdownMenuLabel>
                    {group.threads.map((t) =>
                      renamingId === t.remoteId ? (
                        <div key={t.remoteId} className="flex items-center gap-2 p-2">
                          <div className="flex size-6 shrink-0 items-center justify-center rounded-md border">
                            {t.visibility === 'public' ? (
                              <Globe className="size-3.5" />
                            ) : (
                              <Lock className="size-3.5" />
                            )}
                          </div>
                          <input
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => {
                              e.stopPropagation();
                              if (e.key === 'Enter') void handleRenameSave();
                              if (e.key === 'Escape') handleRenameCancel();
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onBlur={() => void handleRenameSave()}
                            className="flex-1 min-w-0 bg-transparent text-sm outline-none"
                            autoFocus
                          />
                        </div>
                      ) : (
                        <div
                          key={t.remoteId}
                          className={cn(
                            'flex items-center gap-2 p-2 rounded-sm text-sm cursor-pointer transition-colors hover:bg-muted hover:text-foreground group/thread-item',
                            t.remoteId === currentRemoteId && activeView !== 'home' && activeView !== 'inbox' && activeView !== 'documents' && activeView !== 'settings' && activeView !== 'scheduledRuns' && activeView !== 'memory' && 'bg-[var(--axiom-surface-accent)]',
                          )}
                          onClick={() => handleSelect(t.remoteId)}
                        >
                          <div className="flex size-6 shrink-0 items-center justify-center rounded-md border">
                            {t.visibility === 'public' ? (
                              <Globe className="size-3.5" />
                            ) : (
                              <Lock className="size-3.5" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1 truncate">{t.title}</div>
                          {t.updatedAt ? (
                            <div className="shrink-0 text-xs text-muted-foreground">
                              {relativeTime(t.updatedAt)}
                            </div>
                          ) : null}
                          <ThreadItemMore
                            remoteId={t.remoteId}
                            title={t.title}
                            visibility={t.visibility}
                            onRename={handleRenameStart}
                            onDelete={handleDelete}
                            onToggleVisibility={handleToggleVisibility}
                          />
                        </div>
                      ),
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {displayThreads.length === 0 && search.trim() && (
            <>
              <DropdownMenuSeparator />
              <p className="px-2 py-4 text-center text-xs text-muted-foreground">
                No threads found.
              </p>
            </>
          )}

          <DropdownMenuSeparator />

          <DropdownMenuItem className="gap-2 p-2 hover:bg-muted hover:text-foreground focus:bg-muted focus:text-foreground" onSelect={handleNewThread}>
            <div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
              <SquarePenIcon className="size-3.5" />
            </div>
            <span className="font-medium text-muted-foreground">New Chat</span>
          </DropdownMenuItem>

          {(onHomeClick || onInboxClick || onDocumentsClick || onScheduledRunsClick || onMemoryClick || onSettingsClick) && <DropdownMenuSeparator />}

          {onHomeClick && (
            <DropdownMenuItem
              className={cn(
                'gap-2 p-2 hover:bg-muted hover:text-foreground focus:bg-muted focus:text-foreground',
                activeView === 'home' && 'bg-[var(--axiom-surface-accent)]',
              )}
              onSelect={() => {
                onHomeClick();
                setOpen(false);
              }}
            >
              <div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
                <HomeIcon className="size-3.5" />
              </div>
              <span className="font-medium text-muted-foreground">All conversations</span>
            </DropdownMenuItem>
          )}

          {onInboxClick && (
            <DropdownMenuItem
              className={cn(
                'gap-2 p-2 hover:bg-muted hover:text-foreground focus:bg-muted focus:text-foreground',
                activeView === 'inbox' && 'bg-[var(--axiom-surface-accent)]',
              )}
              onSelect={() => {
                onInboxClick();
                setOpen(false);
              }}
            >
              <div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
                <InboxIcon className="size-3.5" />
              </div>
              <span className="font-medium text-muted-foreground">Inbox</span>
            </DropdownMenuItem>
          )}

          {onDocumentsClick && (
            <DropdownMenuItem
              className={cn(
                'gap-2 p-2 hover:bg-muted hover:text-foreground focus:bg-muted focus:text-foreground',
                activeView === 'documents' && 'bg-[var(--axiom-surface-accent)]',
              )}
              onSelect={() => {
                onDocumentsClick();
                setOpen(false);
              }}
            >
              <div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
                <FileText className="size-3.5" />
              </div>
              <span className="font-medium text-muted-foreground">Documents</span>
            </DropdownMenuItem>
          )}

          {onScheduledRunsClick && (
            <DropdownMenuItem
              className={cn(
                'gap-2 p-2 hover:bg-muted hover:text-foreground focus:bg-muted focus:text-foreground',
                activeView === 'scheduledRuns' && 'bg-[var(--axiom-surface-accent)]',
              )}
              onSelect={() => {
                onScheduledRunsClick();
                setOpen(false);
              }}
            >
              <div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
                <Calendar className="size-3.5" />
              </div>
              <span className="font-medium text-muted-foreground">Scheduled runs</span>
            </DropdownMenuItem>
          )}

          {onMemoryClick && (
            <DropdownMenuItem
              className={cn(
                'gap-2 p-2 hover:bg-muted hover:text-foreground focus:bg-muted focus:text-foreground',
                activeView === 'memory' && 'bg-[var(--axiom-surface-accent)]',
              )}
              onSelect={() => {
                onMemoryClick();
                setOpen(false);
              }}
            >
              <div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
                <BrainIcon className="size-3.5" />
              </div>
              <span className="font-medium text-muted-foreground">Memory</span>
            </DropdownMenuItem>
          )}

          {onSettingsClick && (
            <DropdownMenuItem
              className={cn(
                'gap-2 p-2 hover:bg-muted hover:text-foreground focus:bg-muted focus:text-foreground',
                activeView === 'settings' && 'bg-[var(--axiom-surface-accent)]',
              )}
              onSelect={() => {
                onSettingsClick();
                setOpen(false);
              }}
            >
              <div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
                <SettingsIcon className="size-3.5" />
              </div>
              <span className="font-medium text-muted-foreground">Settings</span>
            </DropdownMenuItem>
          )}

          {embedUser && (
            <>
              <DropdownMenuSeparator />
              <div className="flex items-center gap-2 px-2 py-2">
                {embedUser.picture ? (
                  <img
                    src={embedUser.picture}
                    alt={embedUser.name ?? ''}
                    className="size-7 shrink-0 rounded-full"
                  />
                ) : (
                  <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium">
                    {(embedUser.name ?? embedUser.email ?? '?').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="grid min-w-0 flex-1 text-xs leading-tight">
                  <span className="truncate font-medium">
                    {embedUser.name ?? 'User'}
                    {' '}
                    <span className="font-normal text-muted-foreground capitalize">
                      ({embedUser.role || 'User'})
                    </span>
                  </span>
                  {embedUser.email && (
                    <span className="truncate text-muted-foreground">{embedUser.email}</span>
                  )}
                </div>
                {onRefresh && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRefresh();
                    }}
                    className="shrink-0 cursor-pointer rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    aria-label="Refresh"
                    title="Refresh"
                  >
                    <RefreshCw className="size-3.5" />
                  </button>
                )}
                {onSignOut && (
                  <button
                    onClick={onSignOut}
                    className="shrink-0 cursor-pointer rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    aria-label="Sign out"
                    title="Sign out from Google"
                  >
                    <LogOut className="size-3.5" />
                  </button>
                )}
                {!onSignOut && onSignIn && (
                  <button
                    onClick={onSignIn}
                    className="shrink-0 cursor-pointer rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    aria-label="Sign in"
                    title="Sign in with Google"
                  >
                    <LogIn className="size-3.5" />
                  </button>
                )}
              </div>
            </>
          )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="ml-auto flex shrink-0 items-center gap-1.5 pr-1">
        {actions}
      </div>
    </div>
  );
};

const ThreadItemMore: FC<{
  remoteId: string;
  title: string;
  visibility?: 'public' | 'private';
  onRename: (remoteId: string, title: string) => void;
  onDelete: (remoteId: string) => void;
  onToggleVisibility: (remoteId: string, currentVisibility: string | undefined) => void;
}> = ({ remoteId, title, visibility, onRename, onDelete, onToggleVisibility }) => {
  const handleCopyThreadId = () => {
    if (!remoteId) return;
    navigator.clipboard.writeText(remoteId);
    toast.success('Thread ID copied to clipboard');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="shrink-0 cursor-pointer rounded-sm p-0.5 opacity-0 text-muted-foreground transition-colors hover:text-foreground group-hover/thread-item:opacity-100 data-[state=open]:opacity-100 data-[state=open]:bg-[var(--axiom-surface-accent)] data-[state=open]:text-[var(--axiom-content-accent)] focus-visible:ring-0 focus-visible:ring-offset-0"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <MoreHorizontalIcon className="size-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={4} onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onClick={handleCopyThreadId}>
          <ClipboardCopy className="size-4" />
          Copy Thread ID
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            onRename(remoteId, title);
          }}
        >
          <PencilIcon className="size-4" />
          Rename
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            onToggleVisibility(remoteId, visibility);
          }}
        >
          {visibility === 'public' ? (
            <>
              <Lock className="size-4" />
              Make private
            </>
          ) : (
            <>
              <Globe className="size-4" />
              Make public
            </>
          )}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(remoteId);
          }}
        >
          <TrashIcon className="size-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
